import type {ConnectionOperation, EntityOperation} from '@github-ui/optimistic-connection'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {RemoveBlockedByMutationMutation} from '@github-ui/server-persisted-queries/types/removeBlockedByMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {
  BLOCKED_BY_CONNECTION_PATH,
  BLOCKING_CONNECTION_PATH,
} from '../components/sections/relations-section/dependency-issue-query-types'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {invalidateRelayDependencyConnections, writeConfirmedDependencySide} from './add-blocked-by-mutation'

type DependencyDirection = 'blockedBy' | 'blocking'
type RemoveBlockedByVariables = {issueId: string; nameWithOwner: string; issueNumber: number}
type RemoveBlockedByOperation = Extract<ConnectionOperation, {type: 'removeFromConnection'}>
type CountPublication = {
  completed: (operation: EntityOperation) => void
  failed: (error: Error) => void
}
type MutationContext = {relayUpdate: Disposable; countPublication?: CountPublication}
type MutationOptions = Omit<
  Parameters<typeof useConnectionMutation<RemoveBlockedByMutationMutation, Error, MutationContext>>[0],
  'ownerId' | 'connectionPath'
>

function connectionPath(direction: DependencyDirection) {
  return direction === 'blockedBy' ? BLOCKED_BY_CONNECTION_PATH : BLOCKING_CONNECTION_PATH
}

function directionFor(operation: RemoveBlockedByOperation): DependencyDirection {
  return operation.connectionPath[0] === 'topBlocking' ? 'blocking' : 'blockedBy'
}

function toOptimisticOperation(dependencyIssueId: string, direction: DependencyDirection): RemoveBlockedByOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: connectionPath(direction),
    nodeId: dependencyIssueId,
    counts: [{path: ['issueDependenciesSummary', direction], delta: -1}],
  }
}

async function removeBlockedByRequest({
  nameWithOwner,
  issueNumber,
  dependencyIssueId,
  direction,
}: RemoveBlockedByVariables & {dependencyIssueId: string; direction: DependencyDirection}) {
  const relationship = direction === 'blockedBy' ? 'blocked_by' : 'blocking'
  const body = direction === 'blockedBy' ? {blockingIssueId: dependencyIssueId} : {issueId: dependencyIssueId}
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relationships/${relationship}.json`, {
    method: 'DELETE',
    body,
  })
  const result = (await response.json()) as {
    data?: RemoveBlockedByMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message
  if (!response.ok || error || !result.data?.removeBlockedBy?.issue || !result.data.removeBlockedBy.blockingIssue) {
    throw new Error(error ?? 'Failed to remove dependency')
  }
  return result.data
}

export function optimisticRemoveBlockedBy(
  store: RecordSourceProxy,
  issueId: string,
  dependencyIssueId: string,
  direction: DependencyDirection,
) {
  const issueRecord = store.get(issueId)
  if (!issueRecord) return

  const connectionField = direction === 'blockedBy' ? 'blockedBy' : 'blocking'
  const connection = issueRecord.getLinkedRecord(connectionField, {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    const filteredNodes = existingNodes.filter(node => node?.getDataID() !== dependencyIssueId)
    connection.setLinkedRecords(filteredNodes, 'nodes')
  }

  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  if (summary) {
    const currentCount = (summary.getValue(connectionField) as number) || 0
    summary.setValue(Math.max(0, currentCount - 1), connectionField)
  }
}

export function useRemoveBlockedByMutation({issueId, nameWithOwner, issueNumber}: RemoveBlockedByVariables) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const mutationOptions = {
    mutationFn: operation => {
      const removeOperation = operation as RemoveBlockedByOperation
      return removeBlockedByRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        dependencyIssueId: removeOperation.nodeId,
        direction: directionFor(removeOperation),
      })
    },
    onMutate: ({operation}) => {
      const removeOperation = operation as RemoveBlockedByOperation
      const direction = directionFor(removeOperation)
      const countOperation: EntityOperation | undefined =
        direction === 'blockedBy'
          ? {type: 'deltaEntityField', id: issueId, field: 'blockedBy', delta: -1, clamp: {min: 0}}
          : undefined
      return {
        relayUpdate: environment.applyUpdate({
          storeUpdater: store => optimisticRemoveBlockedBy(store, issueId, removeOperation.nodeId, direction),
        }),
        countPublication: countOperation ? publishOptimisticFields(countOperation) : undefined,
      }
    },
    onSuccess: async (data, variables, context) => {
      const payload = data.removeBlockedBy
      const removeOperation = variables.operation as RemoveBlockedByOperation
      const direction = directionFor(removeOperation)
      const currentIssue = direction === 'blockedBy' ? payload?.issue : payload?.blockingIssue
      try {
        if (currentIssue) {
          await settleConfirmedOperation({
            queryClient,
            targets: [{queryKey: secondaryQueryKey}],
            ownerId: issueId,
            operation: {
              type: 'setFields',
              id: issueId,
              fields: {
                topBlockedBy: currentIssue.topBlockedBy,
                topBlocking: currentIssue.topBlocking,
                issueDependenciesSummary: currentIssue.issueDependenciesSummary,
              },
            },
          })
        }
        await queryClient.invalidateQueries({queryKey: pickerQueryKey})
        commitLocalUpdate(environment, store => {
          if (payload?.issue) writeConfirmedDependencySide(store, payload.issue)
          if (payload?.blockingIssue) writeConfirmedDependencySide(store, payload.blockingIssue)
          invalidateRelayDependencyConnections(
            store,
            payload?.issue?.id ?? (direction === 'blockedBy' ? issueId : removeOperation.nodeId),
            payload?.blockingIssue?.id ?? (direction === 'blocking' ? issueId : removeOperation.nodeId),
          )
        })
        const blockedByCount = payload?.issue?.issueDependenciesSummary.blockedBy
        if (context?.countPublication && typeof blockedByCount === 'number' && payload?.issue) {
          context.countPublication.completed({
            type: 'setFields',
            id: payload.issue.id,
            fields: {blockedBy: blockedByCount},
          })
        }
      } finally {
        context?.relayUpdate.dispose()
      }
    },
    onError: (error, _variables, context) => {
      context?.countPublication?.failed(error)
      context?.relayUpdate.dispose()
    },
  } satisfies MutationOptions

  const {mutateAsync: mutateBlockedBy} = useConnectionMutation<RemoveBlockedByMutationMutation, Error, MutationContext>(
    {
      ...mutationOptions,
      ownerId: issueId,
      connectionPath: BLOCKED_BY_CONNECTION_PATH,
    },
  )
  const {mutateAsync: mutateBlocking} = useConnectionMutation<RemoveBlockedByMutationMutation, Error, MutationContext>({
    ...mutationOptions,
    ownerId: issueId,
    connectionPath: BLOCKING_CONNECTION_PATH,
  })

  return useCallback(
    (dependencyIssueId: string, direction: DependencyDirection) =>
      (direction === 'blockedBy' ? mutateBlockedBy : mutateBlocking)({
        operation: toOptimisticOperation(dependencyIssueId, direction),
      }),
    [mutateBlockedBy, mutateBlocking],
  )
}
